import { useState } from 'react'
import { Botao } from '../../../components/Botao'
import { useDiscordarDoParecer } from '../../../api/queries/apoioDaIa'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { Discordancia } from '../../../types/apoioDaIa'
import { DialogoDeTexto } from './DialogoDeTexto'

/**
 * O "não é isso" da fiscalização sobre um ponto do parecer.
 *
 * **Por que ele precisa existir.** No chamado, discordar já tem forma: o fiscal muda a resposta e o
 * sistema registra que aquele campo não foi aceito. Num parecer de MÊS não há ato equivalente —
 * ninguém "marca" a conferência do mês. Sem lugar para o "não é isso", a discordância vira conversa
 * de corredor, e o próximo leitor abre a mesma tela e lê o mesmo ponto já refutado como se fosse
 * novo.
 *
 * **O que ela não faz, e o texto diz:** não apaga o item, não regenera o parecer e não volta para o
 * prompt. Um parecer que aprendesse com as discordâncias deixaria de ser reproduzível a partir de
 * (versão do prompt, modelo, recorte) — que é a procedência inteira deste módulo — e passaria a
 * escrever o que a fiscalização gosta de ler, que é o oposto de fiscalizar.
 *
 * O motivo é obrigatório e o botão fica desabilitado sem ele, porque discordância sem razão
 * registrada é ruído: quem lê meses depois vê "alguém não gostou" e não sabe se o ponto procede.
 *
 * @param props a competência, o parecer, o item refutado e as discordâncias já registradas
 * @returns a marca das discordâncias e o botão de registrar mais uma
 */
export function DiscordarDoParecer({
  competencia,
  parecerId,
  itemCodigo,
  discordancias,
}: {
  competencia: string
  parecerId: number
  itemCodigo: string | null
  discordancias: Discordancia[]
}) {
  const t = TEXTOS.discordancia
  const [aberto, definirAberto] = useState(false)
  const discordar = useDiscordarDoParecer(competencia)

  const registrar = (motivo: string) => {
    discordar.mutate({ parecerId, itemCodigo, motivo })
    definirAberto(false)
  }

  return (
    <div className="mt-2">
      {discordancias.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {discordancias.map((registro) => (
            <li key={registro.id} className="rounded-md border border-amber-200 bg-amber-50 p-2">
              <p className="text-xs font-medium text-amber-900">{t.marca}</p>
              <p className="mt-0.5 text-sm text-slate-700">{registro.motivo}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {t.autoria(registro.registradoPorNome, dataHora(registro.registradoEm))}
              </p>
            </li>
          ))}
        </ul>
      )}

      <Botao tamanho="pequeno" onClick={() => definirAberto(true)}>
        {t.botao}
      </Botao>

      {aberto && (
        <DialogoDeTexto
          titulo={t.titulo}
          ajuda={t.ajuda}
          rotulo={t.campo}
          /* Dez caracteres é o piso do banco (CHECK da V39), e não uma preferência de tela. */
          minimo={10}
          aviso={t.curto}
          aoConfirmar={registrar}
          aoFechar={() => definirAberto(false)}
        />
      )}
    </div>
  )
}
